import { Injectable } from '@angular/core';

/** ngsw-config.json: lazy group of the pdf.js files (step 4, plan E4) */
export const PDF_VIEWER_ASSET_GROUP = 'pdf-viewer';

interface NgswManifest {
  assetGroups?: { name?: string; urls?: unknown }[];
}

/**
 * The pdf.js files the viewer needs for a saved plan: viewer and worker of the build the browser uses (modern .min or
 * legacy -es5, pdfViewerNeedsLegacyBuild), the German locale and the en-US fallback pdf.js always loads with it
 * (Rev1 W5), wasm decoders (JPX/JBIG2 scans) and standard fonts.
 */
export function pdfViewerWarmupUrls(manifest: unknown, legacy: boolean): string[] {
  const urls = (manifest as NgswManifest | null)?.assetGroups?.find((group) => group.name === PDF_VIEWER_ASSET_GROUP)?.urls;
  if (!Array.isArray(urls)) return [];
  const build = legacy ? /\/(viewer|pdf\.worker)-[^/]+-es5\.mjs$/ : /\/(viewer|pdf\.worker)-[^/]+\.min\.mjs$/;
  return urls.filter(
    (url): url is string =>
      typeof url === 'string' &&
      (build.test(url) || /\/assets\/locale\/(locale\.json|de\/|en-US\/)/.test(url) || /\/assets\/wasm\/[^/]+\.wasm$/.test(url) || /\/assets\/standard_fonts\//.test(url)),
  );
}

/**
 * A saved plan must open offline on a device that never showed a plan online (spec §3). The pdf.js files sit in a lazy
 * service worker group (cached when first fetched, refreshed on updates): this fetches them once through the service
 * worker. Best effort: without a service worker (ng serve) or network nothing happens and nothing fails.
 */
@Injectable({ providedIn: 'root' })
export class PdfViewerWarmup {
  /** test seam */
  fetcher: (url: string) => Promise<Response> = (url) => fetch(url);
  private warmed = false;
  /** one warm-up at a time: two saves, a sign-in and the online event share it (NA-27) */
  private running: Promise<number> | null = null;

  warm(legacy: boolean): Promise<number> {
    if (this.warmed) return Promise.resolve(0);
    this.running ??= this.fetchAll(legacy).finally(() => (this.running = null));
    return this.running;
  }

  private async fetchAll(legacy: boolean): Promise<number> {
    let urls: string[];
    try {
      const response = await this.fetcher(`/ngsw.json?ngsw-cache-bust=${Date.now()}`);
      if (!response.ok) return 0;
      urls = pdfViewerWarmupUrls(await response.json(), legacy);
    } catch {
      return 0;
    }
    let fetched = 0;
    for (const url of urls) {
      try {
        if ((await this.fetcher(url)).ok) fetched++;
      } catch {
        // the next save tries again
      }
    }
    this.warmed = fetched === urls.length && urls.length > 0;
    return fetched;
  }
}
